import { types, flow, getRoot, getParent, getSnapshot } from "mobx-state-tree"
import {
  STATES,
  PARKING_STATUS,
  VALID_PARKING_KEYS,
  PAYMENT_PROVIDERS,
  PARKING_FLOW_TYPE,
} from "~/utilities/constants"
import TRACKING from "~/services/tracking"
import buildPersistedChangesStore from "~/stores/models/persistedChanges"
import buildCustomValidationStore from "~/stores/models/customValidation"
import Network from "~/stores/models/Network"
import {
  getParkingGuiltyChargeAmount,
  chargeParkingTickets,
  getSessionStatusFromCKO,
  savePersistedApplicationState,
  getParkingFees,
  finalize3DSChallengeStripe,
} from "~/services/payments"
import { getUserSessionToken } from "~/stores/parkingTicket/flow/parkingConfig"
import { get, update } from "~/services/parking/ticket"
import { errorToShow } from "~/utilities/error"

const ParkingTicketModel = types
  .model("ParkingTicket", {
    objectId: types.identifier,
    ticketID: types.optional(types.string, ""),
    summons_number: types.optional(types.string, ""),
    plate: types.maybe(types.string),
    plateId: types.maybe(types.string),
    plateNumber: types.maybe(types.string),
    state: types.maybe(types.enumeration(Object.keys(STATES))),
    alert: types.maybe(types.string),
    amountDue: types.maybe(types.string),
    amountPaid: types.maybe(types.string),
    fineAmount: types.maybe(types.string),
    inHearingMessage: types.maybe(types.string),
    inJudgmentMessage: types.maybe(types.string),
    interestAmount: types.optional(types.number, 0),
    penaltyAmount: types.optional(types.number, 0),
    penalty_amount: types.maybe(types.string),
    isLessee: types.maybe(types.string),
    isPastDueMessage: types.maybe(types.string),
    issuedOn: types.maybe(types.string),
    issue_date: types.maybe(types.string),
    make: types.maybe(types.string),
    reductionAmount: types.maybe(types.string),
    violationLocationCounty: types.maybe(types.string),
    violationLocationModifier: types.maybe(types.string),
    violationLocationStreet: types.maybe(types.string),
    violationName: types.maybe(types.string),
    violationType: types.maybe(types.string),
    currentFine: types.maybe(types.string),
    violationCode: types.maybe(types.string),
    violation_code: types.maybe(types.string),
    vehicleType: types.maybe(types.string),
    license_type: types.maybe(types.string),
    serviceFee: types.optional(types.number, 5.99), // Initialize with a default value
    status: types.maybeNull(types.enumeration(Object.values(PARKING_STATUS))),
    fightable: types.optional(types.boolean, false),
    isPaid: types.optional(types.boolean, false),
    processPayment: types.optional(types.boolean, false),
    isUserManaged: types.optional(types.boolean, false),
    createdAt: types.maybeNull(types.Date),
    updatedAt: types.maybeNull(types.Date),
    isArchived: types.optional(types.boolean, false),
    requestStatus: types.optional(Network, {}),
    saveStatus: types.optional(Network, {}),
  })
  .preProcessSnapshot((snapshot) => {
    if (!snapshot) return null
    const parsedSnapshot = {}
    const keys = Object.keys(snapshot)
    keys.forEach((key) => {
      switch (key) {
        case "createdAt":
        case "updatedAt":
          parsedSnapshot[key] = snapshot[key] ? new Date(snapshot[key]) : null
          break
        default:
          parsedSnapshot[key] = snapshot[key]
          break
      }
    })
    return parsedSnapshot
  })
  .views((self) => ({
    get user() {
      const root = getRoot(self)
      return root.auth.user
    },
    get paySubtotal() {
      const { user } = self
      const userCredits = user.credits ? user.credits : 0
      const sum =
        self.serviceFee + self.ticketAmountDue + self.feeForTicket - userCredits
      return sum < 0 ? 0 : sum
    },

    get feeForTicket() {
      return self.ticketAmountDue * 0.03
    },
    get ticketAmountDue() {
      return Number(self.amountDue)
    },
    get isLoading() {
      return self.requestStatus.isLoading || self.saveStatus.isLoading
    },
    get dollarFineAmount() {
      const fineAmount = self.fineAmount || self.currentFine
      return `$${Number(fineAmount).toFixed(2)}`
    },
    get dollarAmountDue() {
      return `$${Number(self.amountDue).toFixed(2)}`
    },
  }))
  .actions((self) => ({
    updateData(data) {
      VALID_PARKING_KEYS.forEach((key) => {
        if (data[key]) self[key] = data[key]
      })
      self.setLastUpdate()
    },
    showToast(message, type) {
      const all = getRoot(self)
      all.ui.toasts.addToast({
        message,
        type,
      })
    },
    fetchServiceFee: flow(function* fetchServiceFee() {
      const root = getRoot(self)
      const { isPayNYCFlow } = root.ui
      const source = isPayNYCFlow ? "payNYC" : "webapp"

      try {
        const pricing = yield getParkingFees(
          null,
          source,
          self.ticketAmountDue,
          self.user.paymentProcessor.type === PAYMENT_PROVIDERS.evervault
        )
        self.serviceFee = pricing.serviceFee
      } catch (error) {
        // Handle the error as needed
        console.error("Error fetching service fee:", error)
        throw error
      }
    }),
    get3DSVerifyAmount: flow(function* remote3DSInitializeGen() {
      // gets the actual charge amount
      const root = getRoot(self)
      const total = yield getParkingGuiltyChargeAmount(
        [self.objectId],
        undefined,
        undefined,
        root.auth.user.email
      )
      return total
    }),

    processTicketPayment: flow(function* processPaymentGen(nonce) {
      const sessionToken = getUserSessionToken(self)
      const root = getRoot(self)
      const { isPayNYCFlow } = root.ui
      const source = isPayNYCFlow ? "payNYC" : "webapp"
      const { user } = root.auth
      const objectIds = [self.objectId]
      const { cardScheme } = root.payment.cko
      try {
        const paymentResult = yield chargeParkingTickets(
          "DASHBOARD_PARKING_PAY",
          objectIds,
          nonce,
          sessionToken,
          source,
          false,
          user.email,
          cardScheme
        )
        const result = paymentResult.res
        const { credits } = paymentResult
        if (result.client_secret) {
          const stripe = window.Stripe(process.env.STRIPE_PUBLIC_KEY)
          const { error, paymentIntent } = yield stripe.confirmCardPayment(
            result.client_secret
          )
          const status = paymentIntent?.status || "failed"
          if (paymentIntent?.id) {
            yield finalize3DSChallengeStripe(
              objectIds,
              user.email, // For returning users to help identify them in the backend
              paymentIntent.id,
              status,
              paymentIntent,
              credits
            )
          }
          if (error) {
            yield finalize3DSChallengeStripe(
              objectIds,
              user.email, // For returning users to help identify them in the backend
              error?.payment_intent?.id,
              status,
              error,
              credits
            )
            const stripeErrorMessage = `Card authentication error: ${error.message}`
            throw stripeErrorMessage
          }
          if (status !== "succeeded") {
            throw new Error(
              "Payment not completed successfully after 3DS authentication."
            )
          }
        }
        // fetch charged tickets and save them to the store
        yield self.fetchTicket(sessionToken)
        if (user.paymentProcessor.type === PAYMENT_PROVIDERS.cko) {
          yield getSessionStatusFromCKO()
          const redirectLink = result?.redirectLink
          if (redirectLink) {
            const ckoSessionId = redirectLink.substring(
              redirectLink.lastIndexOf("sid_")
            )
            const applicationState = getSnapshot(getRoot(self))
            yield savePersistedApplicationState(applicationState, ckoSessionId)
            window.location.replace(redirectLink)
            return "REDIRECTED"
          }
        }

        // Attribute dashboard payments coming from email-origin links
        if (["fromPayableTicketsNotification"].includes(user.origin)) {
          TRACKING.parking.paidFromNycEmail(PARKING_FLOW_TYPE.pay, user.origin)
          TRACKING.parking.submissionConversion(self.objectId, PARKING_FLOW_TYPE.pay, root.ui.currentHost)
        }
      } catch (error) {
        if (user.paymentProcessor.type === PAYMENT_PROVIDERS.cko) {
          getRoot(self).payment.remountFlow()
        }
        const displayError = errorToShow(error, "Error charging ticket")
        console.error("Parking Flow - Error charging ticket: ", displayError)
        throw displayError
      }
      return true
    }),
    fetchTicket: flow(function* handleTicketFetch(sessionToken) {
      try {
        const ticket = yield get(self.objectId, sessionToken)
        self.updateData(ticket)
      } catch (error) {
        throw new Error(`error re-fetching tickets: ${error}`)
      }
    }),
    archive: flow(function* handleTicketUpdate() {
      let success = false
      try {
        if (self.saveStatus.isLoading) {
          return true
        }
        self.saveStatus.requestStart()
        const sessionToken = getUserSessionToken(self)
        const updateObject = { isArchived: true }
        if (self.status === "pending") {
          updateObject.status = "closed"
        }
        yield update(self.objectId, updateObject, sessionToken)
        self.showToast(
          `Ticket ${self.ticketID} archived successfully.`,
          "SUCCESS"
        )
        const parent = getParent(self, 2)
        if (parent) {
          parent.fetchAll()
        }
        self.saveStatus.requestComplete()
        success = true
      } catch (error) {
        self.showToast(`Issue archiving ticket ${self.ticketID}.`, "ERROR")
        if (process.env.WINIT_ENV !== "test") {
          console.error("error archiving a ticket", error)
        }
        success = false
        self.saveStatus.requestError(error)
      }
      return success
    }),
  }))

const PersistedChangesStore = buildPersistedChangesStore({})
const CustomValidationStore = buildCustomValidationStore({
  validationObject: {},
})

const ParkingTicket = types.compose(
  ParkingTicketModel,
  PersistedChangesStore,
  CustomValidationStore
)

export default ParkingTicket
